import { set } from '@ember/object';
import { escapeHtml } from './strings.js';

/**
 * @module beebee-base/lib/utility/misc
 * @description Error handling, visibility/booking and fiscal-printer utilities.
 *
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      lib util — JSON:API error → notify/model.errors rendering,
 *              online-booking visibility check, fiscal-printer registration init
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      ./strings (escapeHtml)
 *   siblings:  sibling lib/utility/*.js modules (all re-exported via ../utility.js barrel)
 *   tests:     utility-wave4-test.js, utility-wave5-test.js, utility-extra-test.js,
 *              misc-fiscal-printer-test.js
 *   gotchas:   `initializeAndCheckRegistratori` reads the global `FiscalPrinter`
 *              (injected by the desktop app) without importing it — guarded by
 *              `typeof FiscalPrinter === 'undefined'` since referencing an
 *              undeclared global directly throws a ReferenceError.
 */


/** Mappatura status HTTP a chiavi i18n (intestazione della notifica) */
const HTTP_STATUS_MESSAGES = Object.freeze({
  0: 'beebeeboard error',
  400: 'invalid data',
  401: 'operation not permitted',
  403: 'permission denied',
  404: 'item not found',
  409: 'operation not possible',
  422: 'invalid data',
  429: 'too many requests',
  500: 'beebeeboard error'
});

/** Mappatura status HTTP a tipo notifica */
const HTTP_STATUS_NOTIFY_TYPE = Object.freeze({
  401: 'warning',
  429: 'warning'
});

/**
 * Estrae il documento di errore JSON:API unificato da un `reason` eterogeneo.
 *
 * L'API restituisce ovunque la stessa forma:
 *   { status?, message?, errors: [ { status, source?{pointer}, title, detail } ] }
 *
 * ma `reason` arriva in tre vesti a seconda del punto di chiamata:
 *   - documento gia' pronto (adapter Ember Data: `body`) → ha `.errors`
 *   - Error di beebeeAjax → porta il documento in `.responseJSON` e lo stato in `.status`
 *   - Error semplice → solo `.message`
 * @private
 */
function extractErrorDoc(reason) {
  if (Array.isArray(reason?.errors)) {
    return reason;
  }
  if (Array.isArray(reason?.responseJSON?.errors)) {
    return reason.responseJSON;
  }
  return null;
}

/**
 * Nome del campo dal source pointer JSON:API (`/data/attributes/<field>`),
 * reso leggibile (snake/kebab → parole). Stringa vuota se non e' un attributo.
 * @private
 */
function fieldFromPointer(pointer) {
  const match = /\/data\/attributes\/(.+)$/.exec(pointer || '');
  return match ? match[1].replace(/[_-]+/g, ' ').trim() : '';
}

/**
 * Riga leggibile per un singolo oggetto errore JSON:API.
 *
 * `detail` e' specifico dell'occorrenza, `title` e' la categoria stabile: si
 * preferisce il primo e si aggiunge il secondo solo quando dice qualcosa di
 * diverso (l'API spesso li pone uguali). Se il pointer indica un campo, lo si
 * antepone in grassetto per disambiguare piu' errori sullo stesso modulo.
 * Tutto e' escapato: le risposte d'errore possono riecheggiare input utente.
 * @private
 */
function formatErrorLine(err) {
  if (!err) {
    return '';
  }
  const title = (err.title || '').trim();
  const detail = (err.detail || '').trim();
  let text = detail || title;
  if (title && detail && title !== detail) {
    text = `${title} — ${detail}`;
  }
  if (!text) {
    return '';
  }
  const safeText = escapeHtml(text);
  const field = fieldFromPointer(err.source?.pointer);
  return field ? `<strong>${escapeHtml(field)}:</strong> ${safeText}` : safeText;
}

/**
 * Mostra una notifica di errore all'utente basata sulla risposta API unificata
 * (JSON:API). Rende un errore "parlante": intestazione ricavata dallo stato HTTP
 * piu' una riga per ciascun oggetto in `errors[]` (campo + detail/title), con
 * fallback al `message` a livello di documento e infine al messaggio dell'Error.
 *
 * @param {Object} notify - Servizio notifiche
 * @param {Object} reason - Documento JSON:API, Error di beebeeAjax o Error semplice
 * @param {Object} intl - Servizio internazionalizzazione
 * @returns {boolean} True se la notifica è stata mostrata
 */
function notifyUserError(notify, reason, intl) {
  if (reason === undefined || reason === null || !notify || typeof intl?.t !== 'function') {
    return false;
  }
  const doc = extractErrorDoc(reason);
  const items = Array.isArray(doc?.errors) ? doc.errors : [];

  // Stato: prima il transport status (numerico sugli Error di beebeeAjax), poi
  // quello del primo oggetto errore, infine quello a livello di documento.
  const status = parseInt(reason.status ?? items[0]?.status ?? doc?.status ?? 0, 10);
  const messageKey = HTTP_STATUS_MESSAGES[status] ?? 'comunication error';
  let notifyType = HTTP_STATUS_NOTIFY_TYPE[status] ?? 'error';
  // The notify service may not expose every level; fall back to `error`, and if
  // even that is missing bail rather than throwing inside a catch handler (which
  // would swallow the original reason and show nothing).
  if (typeof notify[notifyType] !== 'function') {
    notifyType = typeof notify.error === 'function' ? 'error' : null;
  }
  if (!notifyType) {
    return false;
  }
  const headline = intl.t(messageKey);

  // Una riga per errore. Su piu' errori li mostriamo tutti, non solo il primo.
  const lines = items.map(formatErrorLine).filter(Boolean);

  // Nessun errors[] utile: ripiega sul message di documento (handler globale
  // dell'API) e poi sul messaggio grezzo dell'Error.
  if (lines.length === 0) {
    // `reason.title` is the flat `{ status, title }` shape. The pre-rewrite
    // version normalised every reason into exactly that pair and rendered the
    // title as the detail line, so callers still hand it over directly; without
    // this branch their detail silently disappears and the user is left with
    // the generic headline alone.
    const fallback = doc?.message || (typeof reason.title === 'string' ? reason.title : '') || (typeof reason.message === 'string' ? reason.message : '');
    if (fallback) {
      lines.push(escapeHtml(fallback));
    }
  }
  const html = lines.length ? `${headline}<br /><small>${lines.join('<br />')}</small>` : headline;
  notify[notifyType]({
    html
  });
  return true;
}

/**
 * Applica gli errori di validazione JSON:API (HTTP 422) ai campi del model.
 *
 * Le API `*_all` rispondono con:
 *   { "errors": [{ "status": "422",
 *                  "source": { "pointer": "/data/attributes/<field>" },
 *                  "title": "...", "detail": "..." }] }
 *
 * Per ogni errore con un pointer di attributo valido aggiunge il messaggio a
 * `model.errors.<field>` (DS.Errors), cosi' i template che fanno il binding su
 * `item.errors.<field>` colorano di rosso il campo e mostrano il messaggio.
 * Inoltre notifica all'utente con `notify.warning`.
 *
 * @param {Object} model - Ember Data model in errore
 * @param {Object} reason - jqXHR o oggetto errore (cerca responseJSON.errors / errors)
 * @param {Object} notify - Servizio notifiche
 * @returns {boolean} True se almeno un errore di validazione e' stato applicato al model
 */
function applyValidationErrors(model, reason, notify) {
  const errors = reason?.responseJSON?.errors || reason?.errors;
  if (!model || !Array.isArray(errors) || typeof model.errors?.add !== 'function') {
    return false;
  }
  let applied = false;
  const messages = [];
  errors.forEach(err => {
    const match = /\/data\/attributes\/(.+)$/.exec(err?.source?.pointer || '');
    const message = err?.detail || err?.title || '';
    if (match) {
      const field = match[1];
      model.errors.remove(field);
      model.errors.add(field, message);
      applied = true;
    }
    if (message) {
      messages.push(message);
    }
  });
  if (applied && notify) {
    // Escape server-provided detail strings before injecting into notify HTML
    notify.warning({
      html: messages.map(escapeHtml).join('<br />')
    });
  }
  return applied;
}

/**
 * Verifica se mostrare il booking online
 * @param {Object} applicationController - Controller applicazione
 * @param {string|number} status - Status da verificare
 * @returns {boolean}
 */
function checkShowOnlineBooking(applicationController, status) {
  if (!applicationController) {
    return false;
  }
  if (applicationController.isSkiSchool && !applicationController.isGuideMonteRosa) {
    return false;
  }
  if (applicationController.isClinic && status !== 1 && status !== '1') {
    return false;
  }
  return true;
}

/**
 * Inizializza e verifica lo stato dei registratori di cassa
 * @param {Array} registratoriRT - Array di registratori
 * @returns {Promise<Array>} Registratori aggiornati con stato
 */
async function initializeAndCheckRegistratori(registratoriRT) {
  // FiscalPrinter è un global iniettato dall'app desktop: senza guard l'accesso
  // lancia ReferenceError (vedi pattern in emetti-scontrino.js / get-scontrino.js).
  if (typeof FiscalPrinter === 'undefined') {
    return [];
  }
  if (!registratoriRT?.length) {
    return [];
  }
  const updatedRegistratoriRT = [];
  for (const registratoreRT of registratoriRT) {
    try {
      const printer = await FiscalPrinter.initializePrinter(registratoreRT.marca, {});
      set(registratoreRT, 'printer', printer);
      const enable = await printer.checkStatus({
        registratore: registratoreRT
      });
      set(registratoreRT, 'enable', enable.esito);

      // Auto-select if single enabled printer
      if (registratoriRT.length === 1 && enable.esito) {
        set(registratoreRT, 'selected', true);
      }
      updatedRegistratoriRT.push(registratoreRT);
    } catch (error) {
      console.error(`Errore inizializzazione registratore ${registratoreRT.name}:`, error);
    }
  }
  return updatedRegistratoriRT;
}

export { applyValidationErrors, checkShowOnlineBooking, initializeAndCheckRegistratori, notifyUserError };
//# sourceMappingURL=misc.js.map
